/**
 * IPC rules containing density tables for different component, silkscreen and assembly outline properties, package specific properties etc. Note: All values are in um.
 * @module IPCRules
 */
module.exports = {
	////////////////////
	// Density Tables //
	////////////////////
	padGoal: {
		gullWing: {
			//gullwing components with greater than 0.625mm pitch
			toeFilletMaxMedMinGT: [550, 350, 150],
			heelFilletMaxMedMinGT: [450, 350, 250],
			sideFilletMaxMedMinGT: [50, 30, 10],

			//gullwing components with smaller than or equal to 0.625mm pitch
			toeFilletMaxMedMinLTE: [550, 350, 150],
			heelFilletMaxMedMinLTE: [450, 350, 250],
			sideFilletMaxMedMinLTE: [10, -20, -40],

			curtyardExcessMaxMedMin: [500, 250, 100],

			pitchTh: 625
		},

		noLead: {
			toeFilletMaxMedMin: [400, 300, 200],
			heelFilletMaxMedMin: [0.0, 0.0, 0.0],
			sideFilletMaxMedMin: [-40, -40, -40],

			curtyardExcessMaxMedMin: [500, 250, 100]
		},

		chip: {
			//Rectangular or Square-End Components (Capacitors and Resistors) Equal to or Larger than 1608 (0603) (unit: mm)
			toeFilletMaxMedMinGTE: [550, 350, 150],
			heelFilletMaxMedMinGTE: [0.0, 0.0, 0.0],
			sideFilletMaxMedMinGTE: [50, 0.0, -50],
			curtyardExcessMaxMedMinGTE: [500, 250, 100],

			toeFilletMaxMedMinLT: [300, 200, 100],
			heelFilletMaxMedMinLT: [0.0, 0.0, 0.0],
			sideFilletMaxMedMinLT: [50, 0.0, -50],

			curtyardExcessMaxMedMinLT: [200, 150, 100],

			bodyLengthTh: 1608
		},

		chipArray: {//Note: In 7351C proposed densities depends in pitch. Our calculator follows IPC7351B standard.
			toeFilletMaxMedMin: [550, 450, 350],
			heelFilletMaxMedMin: [-50, -70, -100],
			sideFilletMaxMedMin: [-50, -70, -100],

			curtyardExcessMaxMedMin: [500, 250, 100]
		},

		moldedBody: {//Note: toe and heel goals are swapped for this component
			toeFilletMaxMedMin: [800, 500, 200],
			heelFilletMaxMedMin: [250, 150, 70],
			sideFilletMaxMedMin: [10, -50, -100],

			curtyardExcessMaxMedMin: [500, 250, 100]
		},

		melf: {
			toeFilletMaxMedMin: [600, 400, 200],
			heelFilletMaxMedMin: [200, 100, 20],
			sideFilletMaxMedMin: [100, 50, 10],

			curtyardExcessMaxMedMin: [500, 250, 100]
		},

		plcc: {//Note: toe and heel goals are swapped for this component because of J-Lead
			toeFilletMaxMedMin: [550, 350, 150],
			heelFilletMaxMedMin: [100, 0, -100],
			sideFilletMaxMedMin: [50, 30, 10],

			curtyardExcessMaxMedMin: [500, 250, 100]
		},

		smallOutlineFlatLead: {
			toeFilletMaxMedMin: [300, 200, 100],
			heelFilletMaxMedMin: [0.0, 0.0, 0.0],
			sideFilletMaxMedMin: [50, 0.0, -50],

			curtyardExcessMaxMedMin: [200, 150, 100]
		},

		dfn: {
			periphery: [50, 0, -50],
			curtyardExcessMaxMedMin: [500, 250, 100]
		},

		aluminumElectrolytic: {
			toeFilletMaxMedMinGTE: [1000, 700, 400],
			heelFilletMaxMedMinGTE: [0.0, -50, -100],
			sideFilletMaxMedMinGTE: [600, 500, 400],

			toeFilletMaxMedMinLT: [700, 500, 300],
			heelFilletMaxMedMinLT: [0.0, -100, -200],
			sideFilletMaxMedMinLT: [500, 400, 300],

			curtyardExcessMaxMedMin: [1000, 500, 250],

			bodyHeightTh: 10000
		},

		oscillator: {
			cornerConcave: {
				toeFilletMaxMedMin: [350, 250, 150],
				heelFilletMaxMedMin: [100, 0.0, -50],
				sideFilletMaxMedMin: [0.0, 0.0, 0.0],

				curtyardExcessMaxMedMin: [500, 250, 100]
			},

			jLead: {//Note: toe and heel goals are swapped for this component because of J-Lead
				toeFilletMaxMedMin: [550, 350, 150],
				heelFilletMaxMedMin: [100, 0, -100],
				sideFilletMaxMedMin: [50, 30, 10],

				curtyardExcessMaxMedMin: [500, 250, 100]
			},

			lBend: {
				//Note: toe and heel goals are swapped for this component because of L-Bend
				toeFilletMaxMedMin: [100, 0, -100],
				heelFilletMaxMedMin: [550, 350, 150],
				sideFilletMaxMedMin: [10, -20, -40],

				curtyardExcessMaxMedMin: [500, 250, 100] //TODO
			}
		},

		soj: {//Note: toe and heel goals are swapped for this component because of J-Lead
			toeFilletMaxMedMin: [550, 350, 150],
			heelFilletMaxMedMin: [100, 0, -100],
			sideFilletMaxMedMin: [50, 30, 10],

			curtyardExcessMaxMedMin: [500, 250, 100]
		},

		bga: {
			collapsible: {
				//percentage decrease from nominal ball diameter
				//table key is the nominal ball diameter and value is an array contains percentage adjustment (from nominal ball diameter), variation (+), ball size increment (round off to specific increment)
				ballDiameterLookupTable: {
					150: [-0.15, 20, 10],
					170: [-0.15, 30, 50],
					200: [-0.15, 30, 10],
					250: [-0.2, 0, 10],
					300: [-0.2, 0, 50],
					350: [-0.2, 50, 10],
					400: [-0.2, 50, 50],
					450: [-0.2, 50, 50],
					500: [-0.2, 50, 50],
					550: [-0.25, 100, 50],
					600: [-0.25, 50, 50],
					650: [-0.25, 50, 50],
					750: [-0.25, 50, 50]
				},

				//nominal ball diameter range. It is used to find exact key to be used in lookup table if nominal ball diameter input does not match any of the keys.
				//find range for calculated nominal ball diamter (average of min and max) and use the range minimum as the key.
				//e.g., if nominal ball diameter is 470, key to be used should be 450.
				//Note: If the input is greater than max key use max index and if it is smaller than min use min index.
				range: [[150, 170], [170, 200], [200, 250], [250, 300], [300, 350], [350, 400], [400, 450], [450, 500], [500, 550], [550, 600], [600, 650], [650, 750]]
			},

			nonCollapsible: {
				ballDiameterLookupTable: {
					150: [0.05, 30, 10],
					170: [0.05, 30, 10],
					200: [0.05, 30, 10],
					250: [0.1, 50, 10],
					300: [0.1, 50, 10],
					400: [0.1, 50, 50],
					450: [0.1, 50, 50],
					500: [0.1, 50, 50],
					550: [0.15, 50, 50],
					600: [0.15, 50, 50],
					750: [0.15, 50, 50]
				},

				range: [[150, 170], [170, 200], [200, 250], [250, 300], [300, 400], [400, 450], [450, 500], [500, 550], [550, 600], [600, 750]]
			},

			curtyardExcessMaxMedMin: [2000, 1000, 500]
		}
	},

	pthPadGoal: {//same for all pth packages
		holeDiameterFactor: [250, 200, 150]
	},

	///////////////////////////
	// Axial lead attributes //
	///////////////////////////

	//Ref: Table 3-1 in IPC7251 doc
	axialLeadGoal: {
		thresholds: [800, 1200],
		//first 3 elements are lead extensions for different densities and last element is lead bend radius multiplication factor (bend radius = factor * lead diameter)
		range1: [1200, 1000, 800, 1.0], // <=0.8mm
		range2: [2200, 1800, 1500, 1.5], // >0.8mm and <=1.2mm
		range3: [2800, 2400, 2000, 2.0] // >1.2mm
	},

	////////////////////
	// Pad attributes //
	////////////////////

	oblongPadCornerRadiusSize: 0.5, //50% of min(pad width, pad height)
	roundedPadCornerRadiusSizeLimit: 250,

	/////////////////////////////////
	// Assembly outline attributes //
	/////////////////////////////////

	assemblyOutlineStrokeWidth: 120,

	assemblyOutlineMappingTypeToBody : {
		'maximum': 'Maximum',
		'nominal': 'Nominal',
		'minimum': 'Minimum'
	},

	///////////////////////////
	// Silkscreen attributes //
	///////////////////////////

	silkscreenMappingTypeToBody : {
		'maximum': 'Maximum',
		'nominal': 'Nominal',
		'minimum': 'Minimum'
	},

	silkscreenStrokeWidth: 120,
	silkscreenClearance: 254, //gap between silkscreen and pad edge (10 mil)
	silkscreenPinMarkerDotClearance: 254, //gap between dot and pad edge (10 mil)
	dotPinMarkerSize: 500,
	//silkscreen corner clip - percentage of body length or width used for aluminum electrolytic
	//percentage of minimum of body length and width for BGA
	silkscreenCornerClipPercentage: 0.25,

	/////////////////////////////////
	// Package specific attributes //
	/////////////////////////////////

	//for DFN (% of body height)
	cornerClipAmount: 0.3,

	//for TO,DPAK package
	//extended edge tolerence
	extendedEdgeTol: 165,

	///////////////////////////
	// Property attributes   //
	///////////////////////////
	propertyConfig: {
		font: "proportional",
        fontSize: 1270,
        fontUnit: 'μm',
        alignment: 'middle',
        clearance: 635 //in mm
	},

	//////////////////////////////
	// Fabrication attributes   //
	//////////////////////////////
	pthFabrication: {
		minAnnularRingWidth: 50,
		allowance: [600, 500, 400] //TODO: Validate. Is taken from IPC7351C?
	}
};
